import React from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export function ErrorFallback({ message }: { message?: string }) {
  return (
    <div className="py-32 flex items-center justify-center px-4">
      <div className="text-center max-w-lg mx-auto">
        {/* Animated error illustration */}
        <div className="relative mb-8">
          <div className="text-[180px] font-black text-destructive/10 leading-none select-none">
            500
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-destructive/10 flex items-center justify-center animate-pulse">
                <AlertTriangle className="w-12 h-12 text-destructive/60" />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-destructive flex items-center justify-center">
                <span className="text-destructive-foreground text-xs font-bold">
                  !
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-4 mb-8">
          <h1 className="text-lg font-bold text-foreground tracking-tight">
            Oops! Something went wrong
          </h1>
          <p className="text-muted-foreground text-[15px] leading-relaxed max-w-md mx-auto">
            An unexpected error occurred. Don&apos;t worry, you can try
            reloading the page or head back home.
          </p>
          {message && (
            <p className="mt-3 inline-block text-sm text-destructive/80 bg-destructive/5 border border-destructive/10 rounded-md px-3 py-1.5 font-mono">
              {message}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            variant="default"
            size="lg"
            className="w-full sm:w-auto gap-2"
            onClick={() => window.location.reload()}
          >
            <RefreshCw className="w-4 h-4" />
            Reload Page
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="w-full sm:w-auto gap-2"
            onClick={() => (window.location.href = "/")}
          >
            <Home className="w-4 h-4" />
            Back to Home
          </Button>
        </div>

        {/* Help text */}
        <p className="mt-8 text-sm text-muted-foreground">
          Need help?{" "}
          <a
            href="/support"
            className="text-primary hover:underline font-medium"
          >
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Error caught in ErrorBoundary:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback message={this.state.error?.message} />;
    }

    return this.props.children;
  }
}
